/**
 * Phase 86 Plan 86-04 Task 1 — the "Compost" design's sort model, compost
 * counts and generated header sentence
 * (`.planning/design/mytasks-compost/README.md`, "The staleness rule this
 * design is built on" + "Copy").
 *
 * Four pure exports, all reading the same zone clock (`taskRotZone` from
 * `utils/format.ts`) so the rail, the header sentence and the zone bands can
 * never disagree — the handoff is explicit that one query feeds all three,
 * and a count that disagrees between the card and the sentence is the most
 * visible way this page can look broken (86-CONTEXT "Data prerequisite").
 *
 *   - `sortTasksByMode`   — the four sort modes (rot / pressure / due / project).
 *   - `groupTasksByZone`  — buckets a rot-sorted list into non-empty zone groups.
 *   - `compostCounts`     — stale + graveyard counts over a given task list.
 *   - `generateHeaderSentence` — the locked copy, generated from those counts.
 */
import type { MyTaskDto } from '../services/api/tasks';
import { taskRotZone, type TaskRotZone } from './format';

export type CompostSortMode = 'rot' | 'pressure' | 'due' | 'project';

/** Zone order fresh → warm → stale → graveyard → terminal (README "The staleness
 * rule this design is built on"). Terminal sorts last and never gets a band. */
const ZONE_ORDER: readonly TaskRotZone[] = ['fresh', 'warm', 'stale', 'graveyard', 'terminal'];

function zoneOf(task: MyTaskDto): TaskRotZone {
  return taskRotZone(task.lastActivityAt, { isTerminal: task.isTerminal });
}

function zoneIndex(task: MyTaskDto): number {
  const idx = ZONE_ORDER.indexOf(zoneOf(task));
  return idx === -1 ? ZONE_ORDER.length : idx;
}

function dueTime(task: MyTaskDto): number | null {
  if (!task.dueAt) return null;
  const t = new Date(task.dueAt).getTime();
  return Number.isNaN(t) ? null : t;
}

/**
 * Within-zone / flat "pressure" tiebreak (README "The staleness rule this
 * design is built on" + 86-04-PLAN behavior): higher SLA reaction % sorts
 * above lower; a task with NO percentage sorts below one that has any (never
 * collapses null to the front). At equal pressure — including "both have no
 * percentage" — fall through to the due-date tiebreak.
 */
function pressureThenDueCompare(a: MyTaskDto, b: MyTaskDto): number {
  const ap = a.slaReactionPct;
  const bp = b.slaReactionPct;
  if (ap != null || bp != null) {
    if (ap == null) return 1;
    if (bp == null) return -1;
    if (ap !== bp) return bp - ap;
  }
  return dueDateCompare(a, b);
}

/** Earlier due date first; a task with no due date sorts last. */
function dueDateCompare(a: MyTaskDto, b: MyTaskDto): number {
  const ad = dueTime(a);
  const bd = dueTime(b);
  if (ad != null && bd != null) return ad - bd;
  if (ad != null) return -1;
  if (bd != null) return 1;
  return 0;
}

/** Zone order first, then the pressure/due tiebreak within a zone. */
function rotCompare(a: MyTaskDto, b: MyTaskDto): number {
  const za = zoneIndex(a);
  const zb = zoneIndex(b);
  if (za !== zb) return za - zb;
  return pressureThenDueCompare(a, b);
}

/** Project name asc, then task name asc for a deterministic within-project order. */
function projectCompare(a: MyTaskDto, b: MyTaskDto): number {
  const c = a.projectName.localeCompare(b.projectName);
  if (c !== 0) return c;
  return a.taskName.localeCompare(b.taskName);
}

/**
 * Sort a task list by one of the four Compost sort modes. Always returns a
 * new array — never mutates the input.
 *
 * - `rot` (default): zone order, then pressure desc, then due asc within a zone.
 * - `pressure`: the SAME pressure/due tiebreak applied across the whole list,
 *   with NO zone grouping — a stale task with a breaching SLA outranks a
 *   fresh task with none.
 * - `due`: due date ascending across the whole list, no-due last.
 * - `project`: grouped by project name (alphabetical), for the sticky
 *   project-group renderer to consume.
 */
export function sortTasksByMode(tasks: MyTaskDto[], mode: CompostSortMode): MyTaskDto[] {
  const copy = [...tasks];
  switch (mode) {
    case 'rot':
      return copy.sort(rotCompare);
    case 'pressure':
      return copy.sort(pressureThenDueCompare);
    case 'due':
      return copy.sort(dueDateCompare);
    case 'project':
      return copy.sort(projectCompare);
    default:
      return copy;
  }
}

export interface CompostZoneGroup {
  zone: TaskRotZone;
  tasks: MyTaskDto[];
}

/**
 * Buckets `tasks` into zone groups in canonical zone order, skipping any
 * zone with zero tasks entirely (README "Empty states" + 86-04-PLAN "An
 * empty zone renders no band"). Internally re-sorts by `rot` so a caller can
 * pass an arbitrarily-ordered list and still get the right grouping/order.
 */
export function groupTasksByZone(tasks: MyTaskDto[]): CompostZoneGroup[] {
  const sorted = sortTasksByMode(tasks, 'rot');
  const buckets = new Map<TaskRotZone, MyTaskDto[]>();
  for (const task of sorted) {
    const zone = zoneOf(task);
    const bucket = buckets.get(zone);
    if (bucket) bucket.push(task);
    else buckets.set(zone, [task]);
  }
  const groups: CompostZoneGroup[] = [];
  for (const zone of ZONE_ORDER) {
    const bucket = buckets.get(zone);
    if (bucket && bucket.length > 0) groups.push({ zone, tasks: bucket });
  }
  return groups;
}

export interface CompostCounts {
  staleCount: number;
  graveyardCount: number;
}

/**
 * Stale + graveyard counts over `tasks` — always over whatever list the
 * caller passes ("the currently visible list" per 86-04-PLAN, i.e. after
 * every other filter has already been applied). Terminal tasks are excluded
 * from both counts: done/archived work never rots.
 */
export function compostCounts(tasks: MyTaskDto[]): CompostCounts {
  let staleCount = 0;
  let graveyardCount = 0;
  for (const task of tasks) {
    const zone = zoneOf(task);
    if (zone === 'stale') staleCount += 1;
    else if (zone === 'graveyard') graveyardCount += 1;
  }
  return { staleCount, graveyardCount };
}

const SMALL_NUMBER_WORDS = [
  'zero',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten',
  'eleven',
  'twelve',
] as const;

/** Spell out 0-12; fall back to digits above that "small" range, per
 * 86-04-PLAN ("spell out small numbers and fall back to digits above the
 * small range"). */
function spellNumber(n: number): string {
  if (Number.isInteger(n) && n >= 0 && n < SMALL_NUMBER_WORDS.length) {
    return SMALL_NUMBER_WORDS[n]!;
  }
  return String(n);
}

function capitalize(word: string): string {
  return word.length === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1);
}

/**
 * The generated header sentence (README "Copy"): `n = stale + graveyard`.
 *
 * The handoff specifies one sentence — "{N} of your {total} tasks are starting
 * to smell" — which its own sample data (4 of 7, mixed zones) reads well. Petra
 * UAT 2026-08-18 hit two cases it does not cover, on a roster where all eight
 * tasks sat in the graveyard:
 *
 *   1. "Eight of your eight" is not how English says "all of them".
 *   2. "starting to smell" describes the STALE zone. Tasks quiet for 15-91 days
 *      are not starting anything; the header was reporting a milder state than
 *      the page below it was showing.
 *
 * So the verb follows whichever zone is actually present, and the subject
 * collapses to "All …" when nothing is left out:
 *
 *   graveyard only → "…have been abandoned"      stale only → "…are starting to smell"
 *   both           → "…are rotting"              none       → "Nothing rotting. Suspiciously clean."
 *
 * The first word is always capitalised.
 */
export function generateHeaderSentence(counts: CompostCounts, total: number): string {
  const rottingCount = counts.staleCount + counts.graveyardCount;
  if (rottingCount === 0) {
    return 'Nothing rotting. Suspiciously clean.';
  }

  // Mixed zones get the neutral verb: neither "abandoned" nor "starting to
  // smell" is true of the whole set, and overstating is as wrong as understating.
  const plural = rottingCount !== 1;
  let verb: string;
  if (counts.staleCount === 0) {
    verb = plural ? 'have been abandoned' : 'has been abandoned';
  } else if (counts.graveyardCount === 0) {
    verb = plural ? 'are starting to smell' : 'is starting to smell';
  } else {
    verb = 'are rotting'; // both zones present ⇒ rottingCount >= 2, always plural
  }

  const totalWord = spellNumber(total);
  // "All one of your tasks" is nobody's English — a single-task roster that is
  // entirely rotten reads better naming the task than counting it.
  if (rottingCount === total) {
    return total === 1
      ? `${capitalize('your')} only task ${verb}`
      : `All ${totalWord} of your tasks ${verb}`;
  }
  return `${capitalize(spellNumber(rottingCount))} of your ${totalWord} tasks ${verb}`;
}

// ---------------------------------------------------------------------------
// FREE TO GRAB picks (Petra UAT 2026-08-18)
// ---------------------------------------------------------------------------

/**
 * The unassigned band used to report a count, which tells you there is work
 * but not whether any of it is yours to want. These three picks each answer a
 * different "what have I got the head for right now": something small,
 * something burning, something nobody has rescued.
 *
 * There is deliberately no skill match. A task carries no role or skill in
 * this schema, so any claim of "this suits you" would be invented; every
 * reason below is a fact about the task the reader can check.
 */
export type FreeToGrabPickKind = 'quick-win' | 'on-fire' | 'orphan';

export interface FreeToGrabPick {
  kind: FreeToGrabPickKind;
  task: MyTaskDto;
  /** One short fact justifying the pick — rendered under the task name. */
  reason: string;
}

const DAY_MS = 86_400_000;

function daysQuiet(task: MyTaskDto, now: number): number {
  const t = new Date(task.lastActivityAt ?? '').getTime();
  return Number.isNaN(t) ? 0 : Math.floor((now - t) / DAY_MS);
}

/** Whichever pressure signal the task actually carries, or null if none does. */
function pressureReason(task: MyTaskDto, now: number): string | null {
  if (task.slaReactionPct != null) {
    const tierName = task.tier?.name;
    return tierName ? `${tierName} · SLA R ${Math.round(task.slaReactionPct)}%` : `SLA R ${Math.round(task.slaReactionPct)}%`;
  }
  if (task.priority === 'urgent') return 'marked urgent';
  if (task.dueAt) {
    const due = new Date(task.dueAt).getTime();
    if (!Number.isNaN(due)) {
      const days = Math.round((due - now) / DAY_MS);
      if (days < 0) return `${Math.abs(days)}d overdue`;
      if (days === 0) return 'due today';
      if (days <= 7) return `due in ${days}d`;
    }
  }
  return null;
}

/**
 * Up to three picks, never the same task twice.
 *
 * Assigned in descending order of how much the signal should override the
 * reader's mood — a burning task is worth interrupting for, a small one is
 * only worth offering. A category with no candidate left is dropped rather
 * than filled with a weaker one, so three cards never imply three good
 * reasons when there is only one.
 */
export function pickFreeToGrab(tasks: MyTaskDto[], now: number = Date.now()): FreeToGrabPick[] {
  const picks: FreeToGrabPick[] = [];
  const taken = new Set<number>();
  const available = () => tasks.filter((t) => !taken.has(t.taskId));

  const onFire = available()
    .map((t) => ({ t, reason: pressureReason(t, now) }))
    .filter((c): c is { t: MyTaskDto; reason: string } => c.reason !== null)
    .sort((a, b) => (b.t.slaReactionPct ?? -1) - (a.t.slaReactionPct ?? -1))[0];
  if (onFire) {
    picks.push({ kind: 'on-fire', task: onFire.t, reason: onFire.reason });
    taken.add(onFire.t.taskId);
  }

  const quick = available()
    .filter((t) => t.estimatedHours != null && t.estimatedHours > 0)
    .sort((a, b) => (a.estimatedHours ?? 0) - (b.estimatedHours ?? 0))[0];
  if (quick) {
    picks.push({ kind: 'quick-win', task: quick, reason: `estimated ${quick.estimatedHours}h` });
    taken.add(quick.taskId);
  }

  const orphan = available().sort((a, b) => daysQuiet(b, now) - daysQuiet(a, now))[0];
  if (orphan) {
    const days = daysQuiet(orphan, now);
    picks.push({
      kind: 'orphan',
      task: orphan,
      reason: days > 0 ? `${days} days unowned` : 'nobody has picked it up',
    });
    taken.add(orphan.taskId);
  }

  // Cards read left-to-right small → burning → forgotten, which is the order
  // the design shows; the assignment order above is only about precedence.
  const order: FreeToGrabPickKind[] = ['quick-win', 'on-fire', 'orphan'];
  return picks.sort((a, b) => order.indexOf(a.kind) - order.indexOf(b.kind));
}
